import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="music-upload"
//
// Caps the number of selected files, then reads existing ID3 tags for each
// (server-side, via MetadataManagerService) so the user can edit them before
// the real upload happens. Edits are submitted alongside the files and
// written into each file's tags server-side in MusicUploadService.
export default class extends Controller {
  static targets = ["input", "list", "template", "notice"]
  static values = { readMetadataUrl: String, maxFiles: Number, truncatedNotice: String }

  filesSelected() {
    let files = Array.from(this.inputTarget.files)

    if (files.length > this.maxFilesValue) {
      files = files.slice(0, this.maxFilesValue)
      this.replaceInputFiles(files)
      this.noticeTarget.textContent = this.truncatedNoticeValue
    } else {
      this.noticeTarget.textContent = ""
    }

    this.listTarget.innerHTML = ""
    if (files.length > 0) this.fetchMetadata(files)
  }

  replaceInputFiles(files) {
    const dataTransfer = new DataTransfer()
    files.forEach(file => dataTransfer.items.add(file))
    this.inputTarget.files = dataTransfer.files
  }

  fetchMetadata(files) {
    const formData = new FormData()
    files.forEach(file => formData.append("files[]", file))

    const csrfToken = document.querySelector('meta[name="csrf-token"]').content

    fetch(this.readMetadataUrlValue, {
      method: "POST",
      headers: { "X-CSRF-Token": csrfToken, "Accept": "application/json" },
      body: formData
    })
      .then(response => response.json())
      .then(({ metadata }) => this.renderForms(files, metadata))
      .catch(() => this.renderForms(files, []))
  }

  renderForms(files, metadata) {
    this.listTarget.innerHTML = ""

    files.forEach((file, index) => {
      const tags = (metadata && metadata[index]) || {}
      const fragment = this.templateTarget.content.cloneNode(true)
      const card = fragment.firstElementChild

      this.setFieldText(card, "filename", file.name)
      this.setFieldValue(card, "titleField", tags.title)
      this.setFieldValue(card, "artistField", tags.artist)
      this.setFieldValue(card, "albumField", tags.album)
      this.setFieldValue(card, "genreField", tags.genre)
      this.setFieldValue(card, "yearField", tags.year)
      this.setFieldValue(card, "trackField", tags.track)
      this.setCoverBadge(card, tags.has_cover)

      this.listTarget.appendChild(fragment)
    })
  }

  coverSelected(event) {
    const card = event.target.closest(".card")
    const preview = card && card.querySelector('[data-music-upload-target~="coverPreview"]')
    if (!preview) return

    if (preview.dataset.objectUrl) {
      URL.revokeObjectURL(preview.dataset.objectUrl)
      delete preview.dataset.objectUrl
    }

    const file = event.target.files[0]
    if (!file) {
      preview.classList.add("d-none")
      preview.removeAttribute("src")
      return
    }

    const objectUrl = URL.createObjectURL(file)
    preview.src = objectUrl
    preview.dataset.objectUrl = objectUrl
    preview.classList.remove("d-none")

    this.setCoverBadge(card, false)
  }

  setCoverBadge(card, hasCover) {
    const badge = card.querySelector('[data-music-upload-target~="coverBadge"]')
    if (badge) badge.classList.toggle("d-none", !hasCover)
  }

  setFieldText(card, targetName, value) {
    const field = card.querySelector(`[data-music-upload-target~="${targetName}"]`)
    if (field) field.textContent = value
  }

  setFieldValue(card, targetName, value) {
    const field = card.querySelector(`[data-music-upload-target~="${targetName}"]`)
    if (field && value) field.value = value
  }
}
